import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Divider,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tabs,
  Tab,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  Description as DescriptionIcon,
  CalendarToday as CalendarIcon,
  Image as ImageIcon,
  PictureAsPdf as PdfIcon,
  TableChart as ExcelIcon,
  LockOpen as ReopenIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import auditService from '../../services/auditService';
import { AuditDetail as AuditDetailType } from '../../types/audit.types';
import { formatDateTime } from '../../utils/dateUtils';
import { useAuth } from '../../contexts/AuthContext';

const AuditDetail: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { user } = useAuth();

  const [detalle, setDetalle] = useState<AuditDetailType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [reopenDialogOpen, setReopenDialogOpen] = useState(false);
  const [reopening, setReopening] = useState(false);
  const [reopenError, setReopenError] = useState<string | null>(null);
  const [activeSection, setActiveSection] = useState(0);

  useEffect(() => {
    loadAuditoria();
  }, [id]);

  const loadAuditoria = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await auditService.getById(Number(id));
      setDetalle(response);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Error al cargar auditoría');
      console.error('Error cargando auditoría:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = async () => {
    try {
      setDownloading(true);
      setDownloadError(null);
      await auditService.downloadPDF(Number(id));
    } catch (err: any) {
      setDownloadError(err.response?.data?.error || 'Error al descargar PDF');
      console.error('Error descargando PDF:', err);
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadExcel = async () => {
    try {
      setDownloading(true);
      setDownloadError(null);
      await auditService.downloadExcel(Number(id));
    } catch (err: any) {
      setDownloadError(err.response?.data?.error || 'Error al descargar Excel');
      console.error('Error descargando Excel:', err);
    } finally {
      setDownloading(false);
    }
  };

  const handleReopen = async () => {
    try {
      setReopening(true);
      setReopenError(null);
      await auditService.reopen(Number(id));
      setReopenDialogOpen(false);
      loadAuditoria();
    } catch (err: any) {
      setReopenError(err.response?.data?.error || 'Error al reabrir la auditoría');
    } finally {
      setReopening(false);
    }
  };
  
  const auditoria = detalle?.audit;
  const respuestas = detalle?.responses || [];

  // Helper para convertir valor_respuesta a string mostrable
  const formatRespuesta = (valor: any): string => {
    if (valor === null || valor === undefined) return 'Sin respuesta';

    // Si es un array de objetos (opciones múltiples)
    if (Array.isArray(valor)) {
      return valor.map((v) => formatRespuesta(v)).join(', ');
    }

    // Si es un objeto JSONB con estructura variable
    if (typeof valor === 'object') {
      // Opción múltiple: {opciones: [...]}
      if (valor.opciones && Array.isArray(valor.opciones)) {
        return valor.opciones.join(', ');
      }
      // Prioridad: respuesta > opcion > valor > texto > fecha > hora > label > value
      if (valor.respuesta !== undefined && valor.respuesta !== null) {
        if (typeof valor.respuesta === 'boolean') return valor.respuesta ? 'Sí' : 'No';
        return String(valor.respuesta);
      }
      if (valor.opcion !== undefined && valor.opcion !== null) return String(valor.opcion);
      if (valor.valor !== undefined && valor.valor !== null) return String(valor.valor);
      if (valor.texto !== undefined && valor.texto !== null) return String(valor.texto);
      if (valor.fecha !== undefined && valor.fecha !== null) {
        // Formatear fecha a DD/MM/YYYY
        const d = new Date(valor.fecha);
        if (!isNaN(d.getTime())) {
          const day = String(d.getDate()).padStart(2, '0');
          const month = String(d.getMonth() + 1).padStart(2, '0');
          const year = d.getFullYear();
          return `${day}/${month}/${year}`;
        }
        return String(valor.fecha);
      }
      if (valor.hora !== undefined && valor.hora !== null) {
        // Mostrar hora en formato 24h (sin am/pm)
        const horaStr = String(valor.hora);
        const match = horaStr.match(/^(\d{1,2}):(\d{2})$/);
        if (match) {
          let h = match[1].padStart(2, '0');
          let m = match[2];
          return `${h}:${m}`;
        }
        return horaStr;
      }
      if (valor.label !== undefined && valor.label !== null) return String(valor.label);
      if (valor.value !== undefined && valor.value !== null) return String(valor.value);
      // Si no tiene ninguna propiedad conocida, devolver JSON
      return JSON.stringify(valor);
    }

    if (typeof valor === 'boolean') return valor ? 'Sí' : 'No';
    return String(valor);
  };

  // Helper para detectar respuestas cortas que deben ir en mayúsculas
  const isShortAnswer = (answer: string): boolean => {
    const shortAnswers = ['sí', 'no', 'si', 'excelente', 'bueno', 'regular', 'malo', 'n/a', 'na'];
    return shortAnswers.includes(answer.toLowerCase()) || answer.length <= 3;
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    const tz = detalle?.audit?.business_timezone;
    return formatDateTime(dateString, tz);
  };

  const getEstadoColor = (estado: string) => {
    switch (estado) {
      case 'completed':
        return 'success';
      case 'draft':
        return 'warning';
      case 'in_progress':
        return 'info';
      default:
        return 'default';
    }
  };

  const getEstadoLabel = (estado: string) => {
    switch (estado) {
      case 'completed':
        return 'Completada';
      case 'draft':
        return 'Borrador';
      case 'in_progress':
        return 'En Progreso';
      default:
        return estado;
    }
  };

  // Agrupar respuestas por categoría
  const respuestasPorCategoria = respuestas?.reduce((acc, respuesta) => {
    const categoria = (respuesta as any).category_name || 'Sin categoría';
    if (!acc[categoria]) {
      acc[categoria] = [];
    }
    acc[categoria].push(respuesta);
    return acc;
  }, {} as Record<string, any[]>);

  // Calcular score por sección a partir del formulario y las respuestas
  const sectionScores = (() => {
    const sections = (detalle?.form as any)?.sections;
    if (!sections?.length || !respuestas.length) return [];
    const responseMap = new Map<number, any>(
      respuestas.map((r: any) => [r.question_id, r])
    );
    return sections
      .map((section: any) => {
        const sectionQuestions: any[] = section.questions || [];
        if (!sectionQuestions.length) return null;
        const sectionResponses = sectionQuestions
          .map((q: any) => responseMap.get(q.id))
          .filter(Boolean);
        const answered = sectionResponses.length;
        const total = sectionQuestions.length;
        const pointsObtained = sectionResponses.reduce((sum: number, r: any) => sum + (Number(r.points_obtained) || 0), 0);
        const maxPoints = sectionResponses.reduce((sum: number, r: any) => sum + (Number(r.max_points) || 0), 0);
        const pct = maxPoints > 0 ? Math.round((pointsObtained / maxPoints) * 100) : null;
        return { id: section.id, name: section.name, color: section.color, answered, total, pct };
      })
      .filter(Boolean);
  })();

  // Color de performance según puntaje (rojo / amarillo / verde)
  const getPctColor = (pct: number | null): string =>
    pct === null ? '#bdbdbd' : pct >= 80 ? '#2e7d32' : pct >= 60 ? '#ed6c02' : '#d32f2f';

  // Mapa de color real por categoría desde la definición del formulario (fuente de verdad)
  const categoryColorMap: Record<string, string> = {};
  ((detalle?.form as any)?.categories || []).forEach((c: any) => {
    if (c.name) categoryColorMap[c.name] = c.color || '#9e9e9e';
  });
  // Fallback: si el form no trajo categorías, usar lo que venga en las respuestas
  respuestas.forEach((r: any) => {
    const name = r.category_name || 'Sin categoría';
    if (!categoryColorMap[name] && r.category_color) categoryColorMap[name] = r.category_color;
  });

  // Score por categoría
  const categoryScores = (() => {
    if (!respuestas.length) return [];
    const catMap = new Map<string, { name: string; obtained: number; max: number; count: number }>();
    respuestas.forEach((r: any) => {
      const name = r.category_name || 'Sin categoría';
      if (!catMap.has(name)) catMap.set(name, { name, obtained: 0, max: 0, count: 0 });
      const cat = catMap.get(name)!;
      cat.obtained += Number(r.points_obtained || 0);
      cat.max += Number(r.max_points || 0);
      cat.count++;
    });
    return Array.from(catMap.values()).map(cat => ({
      ...cat,
      color: categoryColorMap[cat.name] || '#9e9e9e',
      pct: cat.max > 0 ? Math.round((cat.obtained / cat.max) * 100) : null,
    }));
  })();

  // Mapa question_id → pregunta (para acceder a config en la tabla)
  const questionMap = new Map<number, any>();
  ((detalle?.form as any)?.sections || []).forEach((s: any) => {
    (s.questions || []).forEach((q: any) => questionMap.set(q.id, q));
  });

  // Respuestas agrupadas solo por sección (tabla plana, sin subdividir en categorías)
  const responsesBySection = (() => {
    const sections = (detalle?.form as any)?.sections;
    if (!sections?.length || !respuestas.length) return null;
    const responseMap = new Map<number, any>(respuestas.map((r: any) => [r.question_id, r]));
    const result: Array<{ section: any; items: any[] }> = [];
    for (const section of sections) {
      const questions: any[] = section.questions || [];
      const items = questions.map((q: any) => responseMap.get(q.id)).filter(Boolean);
      if (items.length > 0) result.push({ section, items });
    }
    return result.length > 0 ? result : null;
  })();

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error || !auditoria) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error || 'No se pudo cargar la auditoría'}
        </Alert>
        <Button
          variant="outlined"
          startIcon={<BackIcon />}
          onClick={() => navigate('/audits')}
        >
          Volver a auditorías
        </Button>
      </Container>
    );
  }

  return (
    <>
    <Container maxWidth="lg" sx={{ py: isMobile ? 2 : 4 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Button
          startIcon={<BackIcon />}
          onClick={() => navigate('/audits')}
          sx={{ mb: 2 }}
        >
          Volver a auditorías
        </Button>

        <Paper sx={{ p: isMobile ? 1.5 : 2 }}>
          {/* Fila 1: título + estado + puntaje */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
            <Box>
              <Typography variant={isMobile ? 'subtitle1' : 'h6'} fontWeight={700} lineHeight={1.2}>
                Auditoría #{auditoria.id} — {(auditoria as any).form_name}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              {(auditoria as any).compliance_percentage !== null && (auditoria as any).compliance_percentage !== undefined && (
                <Typography
                  variant={isMobile ? 'h6' : 'h5'}
                  fontWeight={700}
                  sx={{
                    color: Number((auditoria as any).compliance_percentage) >= 80
                      ? 'success.main'
                      : Number((auditoria as any).compliance_percentage) >= 60
                      ? 'warning.main'
                      : 'error.main'
                  }}
                >
                  {Number((auditoria as any).compliance_percentage).toFixed(1)}%
                </Typography>
              )}
              <Chip
                label={getEstadoLabel((auditoria as any).status)}
                color={getEstadoColor((auditoria as any).status) as any}
                size="small"
              />
              {!(auditoria as any).synchronized && (
                <Chip label="Pendiente sincronización" color="warning" size="small" />
              )}
            </Box>
          </Box>

          <Divider sx={{ mb: 1.5 }} />

          {/* Fila 2: meta info compacta */}
          <Grid container spacing={1}>
            <Grid item xs={6} sm={3}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                <BusinessIcon color="action" sx={{ fontSize: 16, mt: 0.2 }} />
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block">Negocio</Typography>
                  <Typography variant="body2" fontWeight={600} lineHeight={1.3}>
                    {(auditoria as any).business_name}
                  </Typography>
                  {(auditoria as any).business_address && (
                    <Typography variant="caption" color="text.secondary">{(auditoria as any).business_address}</Typography>
                  )}
                </Box>
              </Box>
            </Grid>

            <Grid item xs={6} sm={3}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                <PersonIcon color="action" sx={{ fontSize: 16, mt: 0.2 }} />
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block">Auditor</Typography>
                  <Typography variant="body2" fontWeight={600} lineHeight={1.3}>
                    {(auditoria as any).auditor_name}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={6} sm={3}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                <CalendarIcon color="action" sx={{ fontSize: 16, mt: 0.2 }} />
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block">Inicio</Typography>
                  <Typography variant="body2" lineHeight={1.3}>
                    {formatDate((auditoria as any).start_date)}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            {(auditoria as any).end_date && (
              <Grid item xs={6} sm={3}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                  <CalendarIcon color="action" sx={{ fontSize: 16, mt: 0.2 }} />
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">Completado</Typography>
                    <Typography variant="body2" lineHeight={1.3}>
                      {formatDate((auditoria as any).end_date)}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            )}
          </Grid>

          {/* Observaciones (sólo si existen) */}
          {(auditoria as any).general_observations && (
            <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
              <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" mb={0.5}>
                Observaciones generales
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {(auditoria as any).general_observations}
              </Typography>
            </Box>
          )}
        </Paper>

        {/* Botones de descarga de reportes */}
        {(auditoria as any).status === 'completed' && (
          <Paper sx={{ p: 2, mt: 2 }}>
            {downloadError && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setDownloadError(null)}>
                {downloadError}
              </Alert>
            )}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              <Typography variant="h6" sx={{ flexGrow: 1 }}>
                Reportes
              </Typography>
              <Button
                variant="contained"
                startIcon={<PdfIcon />}
                onClick={handleDownloadPDF}
                disabled={downloading}
                color="error"
              >
                Descargar PDF
              </Button>
              <Button
                variant="contained"
                startIcon={<ExcelIcon />}
                onClick={handleDownloadExcel}
                disabled={downloading}
                color="success"
              >
                Exportar Excel
              </Button>
              {user?.role === 'admin' && (
                <Button
                  variant="outlined"
                  color="warning"
                  startIcon={<ReopenIcon />}
                  onClick={() => setReopenDialogOpen(true)}
                >
                  Reabrir
                </Button>
              )}
            </Box>
            {downloading && (
              <Box sx={{ mt: 2 }}>
                <CircularProgress size={24} />
                <Typography variant="body2" color="text.secondary" sx={{ ml: 2, display: 'inline' }}>
                  Generando reporte...
                </Typography>
              </Box>
            )}
          </Paper>
        )}
      </Box>

      {/* Desglose por Sección */}
      {sectionScores.length > 0 && (auditoria as any).status === 'completed' && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" gutterBottom sx={{ mb: 2, fontWeight: 600 }}>
            Resultado por Sección
          </Typography>
          <Grid container spacing={2}>
            {sectionScores.map((section: any) => (
              <Grid item xs={12} sm={6} md={4} key={section.id}>
                <Paper sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Box sx={{ width: 12, height: 12, bgcolor: section.color || 'primary.main', borderRadius: '50%', flexShrink: 0 }} />
                    <Typography variant="subtitle2" fontWeight={600} noWrap title={section.name}>
                      {section.name}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', mb: 1 }}>
                    <Typography
                      variant="h5"
                      fontWeight="bold"
                      color={
                        section.pct === null ? 'text.disabled'
                          : section.pct >= 80 ? 'success.main'
                          : section.pct >= 60 ? 'warning.main'
                          : 'error.main'
                      }
                    >
                      {section.pct !== null ? `${section.pct}%` : '—'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {section.answered}/{section.total} preguntas
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={section.pct ?? 0}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      bgcolor: `${getPctColor(section.pct)}22`,
                      '& .MuiLinearProgress-bar': { bgcolor: getPctColor(section.pct) },
                    }}
                  />
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* Resultado por Categoría */}
      {categoryScores.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" gutterBottom sx={{ mb: 2, fontWeight: 600 }}>
            Resultado por Categoría
          </Typography>
          <Grid container spacing={2}>
            {categoryScores.map((cat: any) => (
              <Grid item xs={12} sm={6} md={4} key={cat.name}>
                <Paper sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Box sx={{ width: 12, height: 12, bgcolor: cat.color, borderRadius: '50%', flexShrink: 0 }} />
                    <Typography variant="subtitle2" fontWeight={600} noWrap title={cat.name}>
                      {cat.name}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', mb: 1 }}>
                    <Typography
                      variant="h5"
                      fontWeight="bold"
                      sx={{ color: getPctColor(cat.pct) }}
                    >
                      {cat.pct !== null ? `${cat.pct}%` : '—'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {cat.count} preguntas
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={cat.pct ?? 0}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      bgcolor: `${getPctColor(cat.pct)}22`,
                      '& .MuiLinearProgress-bar': { bgcolor: getPctColor(cat.pct) },
                    }}
                  />
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* Respuestas del Formulario */}
      {(responsesBySection
        ? responsesBySection.length > 0
        : (respuestasPorCategoria && Object.keys(respuestasPorCategoria).length > 0)
      ) && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
            Detalle de Respuestas
          </Typography>

          {/* Carrusel por secciones (tabs) */}
          {(() => {
            const getAnswerChipColor = (answer: string): 'success' | 'error' | 'default' => {
              const v = answer.toLowerCase();
              if (v === 'sí' || v === 'si' || v === 'yes') return 'success';
              if (v === 'no') return 'error';
              return 'default';
            };

            // Para preguntas de escala: chip "valor/max" coloreado por rendimiento
            const renderScaleChip = (respuesta: any) => {
              const rv = respuesta.response_value;
              const raw = rv?.valor ?? rv?.value ?? rv?.respuesta ?? null;
              const valor = raw !== null && raw !== undefined ? Number(raw) : null;
              const q = questionMap.get(respuesta.question_id);
              // config puede estar en q.config o q.options (preguntas antiguas)
              const cfg = q?.config || q?.options || {};
              const maxScale = cfg?.max ?? 5;
              const minScale = cfg?.min ?? 1;
              if (valor === null) return <Typography variant="body2" color="text.disabled">—</Typography>;
              const range = maxScale - minScale;
              const pct = range > 0 ? Math.round(((valor - minScale) / range) * 100) : 100;
              const bgColor = getPctColor(pct);
              return (
                <Chip
                  label={`${valor} / ${maxScale}`}
                  size="small"
                  sx={{
                    fontWeight: 700,
                    fontSize: '0.72rem',
                    bgcolor: bgColor,
                    color: '#fff',
                    minWidth: 56,
                  }}
                />
              );
            };

            const renderTable = (items: any[]) => (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{
                        fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase',
                        letterSpacing: '0.05em', color: 'text.secondary',
                        borderBottom: 2, borderColor: 'divider',
                        width: isMobile ? '45%' : '40%', py: 1.5,
                      }}>Pregunta</TableCell>
                      <TableCell sx={{
                        fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase',
                        letterSpacing: '0.05em', color: 'text.secondary',
                        borderBottom: 2, borderColor: 'divider',
                        width: isMobile ? '55%' : '20%', py: 1.5,
                      }}>Respuesta</TableCell>
                      {!isMobile && <TableCell sx={{
                        fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase',
                        letterSpacing: '0.05em', color: 'text.secondary',
                        borderBottom: 2, borderColor: 'divider',
                        width: '27%', py: 1.5,
                      }}>Observaciones</TableCell>}
                      <TableCell align="center" sx={{
                        fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase',
                        letterSpacing: '0.05em', color: 'text.secondary',
                        borderBottom: 2, borderColor: 'divider',
                        width: '13%', py: 1.5,
                      }}>Evidencias</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {items.map((respuesta: any, index: number) => {
                      const isEscala = respuesta.question_type === 'scale' || respuesta.question_type === 'escala';
                      const answerStr = formatRespuesta(respuesta.response_value);
                      const isShort = !isEscala && isShortAnswer(answerStr);
                      const chipColor = getAnswerChipColor(answerStr);
                      return (
                        <TableRow
                          key={respuesta.id || index}
                          sx={{
                            '&:hover': { bgcolor: 'action.hover' },
                            '& td': { borderBottom: '1px solid', borderColor: 'divider' },
                          }}
                        >
                          <TableCell sx={{ verticalAlign: 'top', py: 1.5 }}>
                            <Typography variant="body2" color="text.primary" lineHeight={1.5}>
                              {respuesta.question_text}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ verticalAlign: 'top', py: 1.5 }}>
                            {isEscala ? (
                              renderScaleChip(respuesta)
                            ) : isShort ? (
                              <Chip
                                label={answerStr.toUpperCase()}
                                size="small"
                                color={chipColor}
                                variant={chipColor === 'default' ? 'outlined' : 'filled'}
                                sx={{ fontWeight: 700, fontSize: '0.7rem' }}
                              />
                            ) : (
                              <Typography variant="body2" color="text.primary" lineHeight={1.5}>
                                {answerStr}
                              </Typography>
                            )}
                          </TableCell>
                          {!isMobile && (
                            <TableCell sx={{ verticalAlign: 'top', py: 1.5 }}>
                              {respuesta.comment ? (
                                <Typography variant="body2" color="text.secondary" lineHeight={1.5}>
                                  {respuesta.comment}
                                </Typography>
                              ) : (
                                <Typography variant="body2" sx={{ color: 'text.disabled', fontStyle: 'italic', fontSize: '0.75rem' }}>
                                  —
                                </Typography>
                              )}
                            </TableCell>
                          )}
                          <TableCell align="center" sx={{ verticalAlign: 'top', py: 1.5 }}>
                            {respuesta.evidences && respuesta.evidences.length > 0 ? (
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'center' }}>
                                <Chip
                                  icon={<ImageIcon sx={{ fontSize: '0.9rem !important' }} />}
                                  label={respuesta.evidences.length}
                                  size="small"
                                  color="info"
                                  variant="outlined"
                                  sx={{ fontWeight: 600 }}
                                />
                                {respuesta.evidences.map((evidencia: any, idx: number) => (
                                  <Button
                                    key={idx}
                                    size="small"
                                    variant="text"
                                    href={evidencia.archivo_url || evidencia.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={{ fontSize: '0.7rem', minWidth: 'auto', px: 0.5, py: 0 }}
                                  >
                                    #{idx + 1}
                                  </Button>
                                ))}
                              </Box>
                            ) : (
                              <Typography variant="body2" sx={{ color: 'text.disabled', fontSize: '0.75rem' }}>—</Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            );

            if (responsesBySection) {
              // Carrusel de secciones con Tabs
              const clampedSection = Math.min(activeSection, responsesBySection.length - 1);
              const currentItem = responsesBySection[clampedSection];
              return (
                <>
                  <Tabs
                    value={clampedSection}
                    onChange={(_e, v) => setActiveSection(v)}
                    variant="scrollable"
                    scrollButtons="auto"
                    allowScrollButtonsMobile
                    sx={{ mb: 1, borderBottom: 1, borderColor: 'divider' }}
                  >
                    {responsesBySection.map((item: any, idx: number) => (
                      <Tab
                        key={item.section.id}
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: item.section.color || '#1976d2', flexShrink: 0 }} />
                            <span>{item.section.name}</span>
                            <Chip label={item.items.length} size="small" sx={{ height: 18, fontSize: 10 }} />
                          </Box>
                        }
                        value={idx}
                        sx={{ textTransform: 'none', minHeight: 48 }}
                      />
                    ))}
                  </Tabs>
                  {currentItem && (
                    <Card sx={{ boxShadow: 2 }}>
                      <CardContent sx={{ p: 0 }}>
                        <Box sx={{
                          display: 'flex', alignItems: 'center', gap: 1.5,
                          borderLeft: `6px solid ${currentItem.section.color || '#1976d2'}`,
                          bgcolor: 'background.paper', p: 1.5,
                        }}>
                          <Typography variant="subtitle1" fontWeight={700}>{currentItem.section.name}</Typography>
                          <Typography variant="caption" color="text.secondary">({currentItem.items.length} preguntas)</Typography>
                        </Box>
                        {renderTable(currentItem.items)}
                      </CardContent>
                    </Card>
                  )}
                </>
              );
            }

            // Fallback: sin secciones → tabla única
            return (
              <Card sx={{ boxShadow: 2 }}>
                <CardContent sx={{ p: 0 }}>
                  {renderTable(respuestas as any[])}
                </CardContent>
              </Card>
            );
          })()}
        </Box>
      )}

      {/* Si no hay respuestas */}
      {(responsesBySection ? responsesBySection.length === 0 : (!respuestasPorCategoria || Object.keys(respuestasPorCategoria).length === 0)) && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <DescriptionIcon sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No hay respuestas registradas
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Esta auditoría aún no tiene respuestas completadas.
          </Typography>
        </Paper>
      )}
    </Container>

    {/* Diálogo confirmar reabrir */}
    <Dialog open={reopenDialogOpen} onClose={() => !reopening && setReopenDialogOpen(false)}>
      <DialogTitle>¿Reabrir auditoría?</DialogTitle>
      <DialogContent>
        <DialogContentText>
          La auditoría volverá al estado <strong>Borrador</strong>. Las respuestas ya cargadas se conservarán
          y el auditor podrá continuar completándola.
        </DialogContentText>
        {reopenError && <Alert severity="error" sx={{ mt: 2 }}>{reopenError}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setReopenDialogOpen(false)} disabled={reopening}>Cancelar</Button>
        <Button onClick={handleReopen} color="warning" variant="contained" disabled={reopening}>
          {reopening ? 'Reabriendo...' : 'Sí, reabrir'}
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
};

export default AuditDetail;
